import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetMarqueeDataByIdQuery,
  useUpdateMarqueeDataMutation,
} from "../../../redux/api/MarqueeApi/marqueeApi";
import { FaEdit, FaSave, FaArrowLeft } from "react-icons/fa";
import "./Marquee.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const EditMarquee = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Notice: Updated to use GetMarqueeDataByIdQuery which is more efficient for editing
  const { data: marqueeData, isLoading: isFetching } =
    useGetMarqueeDataByIdQuery(id);
  const [updateMarquee, { isLoading }] = useUpdateMarqueeDataMutation();

  const validationSchema = Yup.object().shape({
    content: Yup.string()
      .trim()
      .required("Content is required")
      .min(3, "Content must be at least 3 characters"),
    display_order: Yup.number(),
    status: Yup.boolean(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      content: marqueeData?.content?.text || marqueeData?.content || "",
      display_order: marqueeData?.display_order || 0,
      status: marqueeData?.status ?? true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await updateMarquee({
          id,
          ...values,
        }).unwrap();
        message.success("Marquee updated successfully!");
        setTimeout(() => navigate("/admin/marquee"), 2000);
      } catch (error) {
        message.error(
          error.data?.error || error.message || "Failed to update marquee",
        );
      }
    },
  });

  if (isFetching || isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaEdit className="text-primary me-2" />
            Edit Marquee
          </h2>
          <p className="text-muted mb-0">Update the announcement details</p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/marquee")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              Marquee Information
            </h5>
            <Row className="g-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Marquee Content <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="content"
                    {...formik.getFieldProps("content")}
                    isInvalid={
                      formik.touched.content && !!formik.errors.content
                    }
                    placeholder="Enter announcement text here..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.content}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    {...formik.getFieldProps("display_order")}
                  />
                  <Form.Text className="text-muted small">
                    Lower numbers appear first in the marquee.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex align-items-center mt-md-auto mt-4">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="marquee-status"
                    label={
                      formik.values.status
                        ? "Enabled / Active"
                        : "Disabled / Inactive"
                    }
                    name="status"
                    checked={formik.values.status}
                    onChange={formik.handleChange}
                    className={`fw-bold ${
                      formik.values.status ? "text-success" : "text-danger"
                    }`}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/marquee")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Update Marquee
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default EditMarquee;
